import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { CookieConsentStatus, getCookieConsent } from '@/utils/cookieConsent';

interface ConsentContextType {
  consentStatus: CookieConsentStatus;
  hasAnalyticsConsent: boolean;
  refreshConsent: () => void;
}

const ConsentContext = createContext<ConsentContextType | undefined>(undefined);

interface ConsentProviderProps {
  children: ReactNode;
}

export const ConsentProvider = ({ children }: ConsentProviderProps) => {
  const [consentStatus, setConsentStatus] = useState<CookieConsentStatus>(getCookieConsent);

  const refreshConsent = () => {
    setConsentStatus(getCookieConsent());
  };

  useEffect(() => {
    const handleConsentChange = () => {
      refreshConsent();
    };

    window.addEventListener('cookieConsentChanged', handleConsentChange);
    return () => {
      window.removeEventListener('cookieConsentChanged', handleConsentChange);
    };
  }, []);

  const hasAnalyticsConsent = consentStatus === 'accepted';

  return (
    <ConsentContext.Provider value={{
      consentStatus,
      hasAnalyticsConsent,
      refreshConsent,
    }}>
      {children}
    </ConsentContext.Provider>
  );
};

export const useConsent = () => {
  const context = useContext(ConsentContext);
  if (context === undefined) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};